import { useState, useEffect, useCallback } from "react";

type ThemeChoice = "light" | "dark" | "system";

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(choice: ThemeChoice): "light" | "dark" {
  return choice === "system" ? getSystemTheme() : choice;
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeChoice>(() => {
    const stored = localStorage.getItem("theme") as ThemeChoice | null;
    return stored === "light" || stored === "dark" ? stored : "system";
  });

  const resolved = resolveTheme(theme);

  const setTheme = useCallback((choice: ThemeChoice) => {
    localStorage.setItem("theme", choice);
    setThemeState(choice);
  }, []);

  const cycle = useCallback(() => {
    const order: ThemeChoice[] = ["light", "dark", "system"];
    const idx = order.indexOf(theme);
    setTheme(order[(idx + 1) % order.length]!);
  }, [theme, setTheme]);

  // Apply to DOM
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
  }, [resolved]);

  // Listen for system preference changes
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      document.documentElement.setAttribute("data-theme", getSystemTheme());
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  return { theme, resolvedTheme: resolved, setTheme, cycle };
}
